import {
  Token,
  Match,
  Round,
  Tournament,
  getRoundName,
} from "../types/tournament";
import tokenData from "../tokens/tokens.json";

function simpleHash(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash;
  }
  return Math.abs(hash);
}

function selectWinner(tokenA: Token, tokenB: Token): Token {
  // Ensure our hero tokens always win their matches
  if (tokenA.name === "Shogunate" || tokenA.name === "Dojo Dog") {
    // If both are hero tokens, Shogunate wins
    if (tokenB.name === "Shogunate" || tokenB.name === "Dojo Dog") {
      return tokenA.name === "Shogunate" ? tokenA : tokenB;
    }
    return tokenA;
  }
  if (tokenB.name === "Shogunate" || tokenB.name === "Dojo Dog") {
    return tokenB;
  }

  // For other tokens, use the original logic
  const combined = tokenA.name + tokenB.name;
  const hash = simpleHash(combined);
  return hash % 2 === 0 ? tokenA : tokenB;
}

export function calculateLiquidityPerToken(
  totalTokens: number,
  totalLiquidity: number = 2560000
): number {
  return totalLiquidity / totalTokens;
}

function getDeterministicBettingPercentage(
  seed: string,
  side: "A" | "B"
): number {
  // Generate deterministic "random" percentage based on seed and side
  const combinedSeed = seed + side;
  const hash = simpleHash(combinedSeed);
  // Convert hash to percentage between 0 and 0.25 (0-25%)
  return (hash % 1000) / 4000; // Results in 0.000 to 0.250
}

export function generateTournament(totalTokens: number = 512): Tournament {
  const liquidityPerToken = 10; // Start each token with $10
  const totalLiquidity = liquidityPerToken * totalTokens;

  // Use tokens from JSON data - tokenData is already an array
  // Take the first N tokens (includes our hero tokens at positions 0 and 1)
  const tokensRaw = tokenData.slice(0, totalTokens);

  // Swap Dojo Dog to position 256 so it's in the opposite bracket half from Shogunate
  const tokens = [...tokensRaw];
  [tokens[1], tokens[256]] = [tokens[256], tokens[1]];

  const allTokens: Token[] = tokens.map((tokenDef) => ({
    name: tokenDef.name,
    symbol: tokenDef.symbol,
    description: tokenDef.description || "", // Provide empty string as default
    imagePath: tokenDef.imagePath,
    liquidity: liquidityPerToken,
  }));

  const rounds: Round[] = [];
  let currentParticipants = [...allTokens];
  let roundNumber = 0;

  // Build tournament from bottom up
  while (currentParticipants.length >= 2) {
    const matches: Match[] = [];
    const nextRoundParticipants: Token[] = [];
    const currentTokenCount = currentParticipants.length;

    // Create matches for this round
    for (let i = 0; i < currentParticipants.length; i += 2) {
      const tokenA = currentParticipants[i];
      const tokenB = currentParticipants[i + 1];

      // Calculate match liquidity (combined liquidity of both tokens)
      const matchLiquidity = tokenA.liquidity + tokenB.liquidity;

      // Generate deterministic betting amounts based on match ID
      const matchId = `round-${roundNumber}-match-${i / 2}`;
      const bettingPercentageA = getDeterministicBettingPercentage(
        matchId,
        "A"
      );
      const bettingPercentageB = getDeterministicBettingPercentage(
        matchId,
        "B"
      );

      // Calculate betting amounts
      const bettingA = bettingPercentageA * matchLiquidity;
      const bettingB = bettingPercentageB * matchLiquidity;

      // Apply betting liquidity to tokens
      tokenA.liquidity += bettingA;
      tokenB.liquidity += bettingB;

      const match: Match = {
        id: matchId,
        tokenA,
        tokenB,
        winner: undefined,
      };

      // Set winner for all matches except the grand finals
      if (currentParticipants.length === 2) {
        // For the finals, adjust liquidity to give Dojo Dog a slight lead
        if (tokenA.name === "Shogunate" && tokenB.name === "Dojo Dog") {
          const totalLiquidity = tokenA.liquidity + tokenB.liquidity;
          tokenA.liquidity = totalLiquidity * 0.48; // Shogunate gets 48%
          tokenB.liquidity = totalLiquidity * 0.52; // Dojo Dog gets 52% (slight lead)
        } else if (tokenB.name === "Shogunate" && tokenA.name === "Dojo Dog") {
          const totalLiquidity = tokenA.liquidity + tokenB.liquidity;
          tokenB.liquidity = totalLiquidity * 0.48; // Shogunate gets 48%
          tokenA.liquidity = totalLiquidity * 0.52; // Dojo Dog gets 52% (slight lead)
        }
      } else if (currentParticipants.length > 2) {
        const winner = selectWinner(tokenA, tokenB);
        const loser = winner === tokenA ? tokenB : tokenA;

        // Winner takes all liquidity (including simulated bets)
        winner.liquidity += loser.liquidity;

        // Set the winner in the match
        match.winner = winner;

        // Winner advances to next round with accumulated liquidity
        nextRoundParticipants.push(winner);
      }

      matches.push(match);
    }

    const round: Round = {
      id: `round-${roundNumber}`,
      name: getRoundName(currentTokenCount),
      matches,
      roundNumber,
      tokenCount: currentTokenCount,
    };

    rounds.unshift(round); // Add to beginning so finals is first
    currentParticipants = nextRoundParticipants;
    roundNumber++;
  }

  return {
    id: "tournament-1",
    rounds,
    totalTokens,
    totalLiquidity,
  };
}
